import React from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { Tooltip } from '@mui/material';
import { useTailwindBrand } from '../../Theme/useTailwindBrand';
import usePartnerAdvice, { PARTNER_ADVICE_STUDENT_THRESHOLD } from '../../Hooks/usePartnerAdvice';

export interface PartnerAdviceBannerProps {
    /** "full" : carte mise en avant (tableau de bord)"compact" : bandeau discret (paramètres) */
    variant?: 'full' | 'compact';
    className?: string;
}

const buildCopy = (count: number) => {
    if (count <= 0) {
        return {
            emoji: '🚀',
            title: 'Trouvez vos premiers élèves !',
            message: 'Votre profil est prêt, il ne manque plus que les élèves. Notre partenaire vous partage des méthodes concrètes pour lancer votre activité et décrocher vos premières heures de cours.',
        };
    }
    if (count === 1) {
        return {
            emoji: '📈',
            title: 'Passez à la vitesse supérieure !',
            message: 'Votre premier élève est là, bravo ! Découvrez maintenant comment en attirer de nouveaux et remplir votre planning chaque semaine.',
        };
    }
    return {
        emoji: '🎯',
        title: 'Votre prochain élève vous attend !',
        message: `Avec ${count} élèves, vous êtes sur la bonne voie. Notre partenaire vous livre ses meilleures stratégies pour continuer à développer votre activité.`,
    };
};

const BENEFITS = ['Conseils concrets', "Stratégies d'acquisition", 'Visibilité renforcée'];

/**
 * Bannière proposant la page partenaire (acquisition d'élèves) aux profs
 * Teach'r ayant moins de 3 élèves. S'affiche toute seule uniquement si éligible.
 */
const PartnerAdviceBanner: React.FC<PartnerAdviceBannerProps> = ({ variant = 'full', className = '' }) => {
    const { classes, brandConfig } = useTailwindBrand();
    const { visible, studentCount, snooze, openPartnerPage } = usePartnerAdvice();

    if (!visible) return null;

    const colors = brandConfig.colors;
    const copy = buildCopy(studentCount);

    if (variant === 'compact') {
        return (
            <div
                className={`animate-fadeIn relative flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 bg-white border-2 rounded-xl p-4 shadow-sm ${classes.primaryBorder} ${classes.fontFamily} ${className}`}
            >
                <div
                    className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center text-white shadow"
                    style={{ background: `linear-gradient(135deg, ${colors.secondary}, ${colors.primary})` }}
                >
                    <RocketLaunchIcon fontSize="small" />
                </div>
                <div className="flex-1 min-w-0 pr-6 sm:pr-0">
                    <p className={`font-bold text-sm md:text-base ${classes.primaryText}`}>
                        {copy.emoji} Besoin de plus d'élèves ?
                    </p>
                    <p className="text-xs md:text-sm text-gray-600">
                        {copy.message}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={openPartnerPage}
                    className="flex-shrink-0 inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full text-white text-sm font-bold shadow-md transition-transform duration-200 hover:scale-105"
                    style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
                >
                    Ça vous intéresse ?
                    <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </button>
                <Tooltip title="Masquer pendant 7 jours">
                    <button
                        type="button"
                        onClick={snooze}
                        aria-label="Masquer"
                        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </button>
                </Tooltip>
            </div>
        );
    }

    return (
        <div
            className={`animate-fadeIn relative overflow-hidden rounded-2xl p-6 md:p-8 text-white shadow-lg ${classes.fontFamily} ${className}`}
            style={{ background: `linear-gradient(120deg, ${colors.primary} 0%, ${colors.secondary} 100%)` }}
        >
            {/* Cercles décoratifs */}
            <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10" />
            <div className="pointer-events-none absolute -bottom-20 -left-10 w-44 h-44 rounded-full bg-white/10" />

            <Tooltip title="Masquer pendant 7 jours">
                <button
                    type="button"
                    onClick={snooze}
                    aria-label="Masquer"
                    className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center transition-colors"
                >
                    <CloseIcon sx={{ fontSize: 18 }} />
                </button>
            </Tooltip>

            <div className="relative flex flex-col lg:flex-row lg:items-center gap-6">
                <div className="flex-1 min-w-0">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 text-xs font-bold uppercase tracking-wide mb-3">
                        <RocketLaunchIcon sx={{ fontSize: 14 }} />
                        Coup de pouce partenaire
                    </span>
                    <h3 className="text-xl md:text-2xl font-extrabold mb-2">
                        {copy.emoji} {copy.title}
                    </h3>
                    <p className="text-sm md:text-base text-white/90 max-w-2xl">
                        {copy.message}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-4">
                        {BENEFITS.map((benefit) => (
                            <span
                                key={benefit}
                                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/15 text-xs font-semibold"
                            >
                                ✓ {benefit}
                            </span>
                        ))}
                    </div>
                </div>

                <div className="flex flex-col items-start lg:items-center gap-4 flex-shrink-0">
                    {/* Objectif : visualisation des places restantes */}
                    <div className="flex flex-col items-start lg:items-center gap-1.5">
                        <div className="flex items-center gap-2">
                            {Array.from({ length: PARTNER_ADVICE_STUDENT_THRESHOLD }, (_, i) => (
                                <div
                                    key={i}
                                    className={`w-9 h-9 rounded-full flex items-center justify-center text-base transition-all ${i < studentCount
                                        ? 'bg-white shadow'
                                        : 'border-2 border-dashed border-white/60 text-white/70'
                                        }`}
                                >
                                    {i < studentCount ? '👤' : '?'}
                                </div>
                            ))}
                        </div>
                        <span className="text-xs font-semibold text-white/80">
                            {studentCount}/{PARTNER_ADVICE_STUDENT_THRESHOLD} élèves - objectif à portée de main
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={openPartnerPage}
                        className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white font-extrabold text-sm md:text-base shadow-lg transition-transform duration-200 hover:scale-105"
                        style={{ color: colors.primary }}
                    >
                        Ça vous intéresse ?
                        <ArrowForwardIcon sx={{ fontSize: 18 }} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PartnerAdviceBanner;
